<script setup lang="ts">
import { CheckboxIndicator, CheckboxRoot } from 'reka-ui'
import { computed, useAttrs, useId, useSlots } from 'vue'

import './base.css'
import EIcon from './EIcon.vue'
import { useFieldContext } from './field-context'

// inheritAttrs is off so aria-label and friends reach the control rather than the
// layout wrapper — see the split below.
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    label?: string
    /** Secondary line under the label. */
    description?: string
    disabled?: boolean
    invalid?: boolean
    /** Header checkbox of a table with a partial selection. */
    indeterminate?: boolean
  }>(),
  { label: '', description: '' },
)

const model = defineModel<boolean>({ default: false })

const slots = useSlots()
const attrs = useAttrs()
const field = useFieldContext()

/*
 * Attributes are split, and `inheritAttrs` is off, because the root element here is a
 * layout wrapper — not the control.
 *
 * Vue applies fallthrough attributes to a component's ROOT element. This component's
 * root is the `<div class="e-checkbox">` that positions the box beside its label, so
 * an `aria-label` passed by a caller landed on that div and the element actually
 * carrying `role="checkbox"` had no accessible name at all. The attribute was
 * accepted, appeared in the DOM, and did nothing — the worst shape an accessibility
 * bug can take, because it looks handled.
 *
 * Layout attributes (class, style) belong on the wrapper; everything else belongs on
 * the control.
 */
const wrapperAttrs = computed(() => ({ class: attrs['class'], style: attrs['style'] }))
const controlAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})
const fallbackId = useId()

/*
 * A checkbox with its OWN label owns its own id; only a lone, unlabelled one adopts
 * the enclosing EField's control id.
 *
 * The distinction matters because several checkboxes commonly share one EField — a
 * "limit to particular vendors" list, say. Taking the field's controlId
 * unconditionally gave all of them the same id, so every `<label for>` in the group
 * pointed at the first input and a screen reader announced twenty-six checkboxes as
 * twenty-six copies of the field's label. Sighted users saw the right words; only
 * assistive technology and a test locating by accessible name saw the collision
 * (WCAG 2.1 AA, §11).
 *
 * When the checkbox has no label of its own it IS the field's sole control and the
 * field's label is its accessible name, so it keeps the field's id — otherwise
 * EField's `<label for>` would point at nothing. Group several of these inside an
 * EFieldset, which renders a real fieldset/legend.
 */
const ownsLabel = computed(() => props.label !== '' || slots.default !== undefined)
const id = computed(() =>
  ownsLabel.value ? fallbackId : (field?.controlId.value ?? fallbackId),
)
const isDisabled = computed(() => props.disabled === true || field?.disabled.value === true)
const isInvalid = computed(() => props.invalid === true || field?.invalid.value === true)

const state = computed<boolean | 'indeterminate'>({
  get: () => (props.indeterminate ? 'indeterminate' : model.value),
  set: (value) => {
    model.value = value === 'indeterminate' ? false : value
  },
})
</script>

<template>
  <div
    class="e-checkbox"
    :class="[{ 'e-checkbox--disabled': isDisabled }, wrapperAttrs.class]"
    :style="wrapperAttrs.style"
  >
    <CheckboxRoot
      v-bind="controlAttrs"
      :id="id"
      v-model="state"
      class="e-checkbox__box"
      :disabled="isDisabled"
      :aria-invalid="isInvalid ? 'true' : undefined"
      :aria-describedby="field?.describedBy.value"
    >
      <CheckboxIndicator class="e-checkbox__mark">
        <EIcon :name="indeterminate ? 'minus' : 'check'" :size="12" :stroke-width="2.5" />
      </CheckboxIndicator>
    </CheckboxRoot>

    <label v-if="label !== '' || $slots.default" :for="id" class="e-checkbox__label">
      <span><slot>{{ label }}</slot></span>
      <span v-if="description !== ''" class="e-checkbox__description">{{ description }}</span>
    </label>
  </div>
</template>

<style>
.e-checkbox {
  display: flex;
  align-items: flex-start;
  gap: var(--erpx-space-2);
  min-width: 0;
}

.e-checkbox__box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 16px;
  height: 16px;
  margin-block-start: 2px;
  background-color: var(--erpx-color-surface-raised);
  border: 1px solid var(--erpx-color-border-strong);
  border-radius: var(--erpx-radius-sm);
  color: var(--erpx-color-accent-on-solid);
  cursor: pointer;
  transition:
    background-color var(--erpx-duration-fast) var(--erpx-easing-standard),
    border-color var(--erpx-duration-fast) var(--erpx-easing-standard);
}

.e-checkbox__box:focus-visible {
  outline: none;
  box-shadow: var(--erpx-focus-ring);
}

.e-checkbox__box[data-state='checked'],
.e-checkbox__box[data-state='indeterminate'] {
  background-color: var(--erpx-color-accent-solid);
  border-color: var(--erpx-color-accent-solid);
}

.e-checkbox__box[data-disabled] {
  background-color: var(--erpx-color-surface-disabled);
  border-color: var(--erpx-color-border-subtle);
  cursor: not-allowed;
}

.e-checkbox__mark {
  display: inline-flex;
}

.e-checkbox__label {
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: var(--erpx-text-base);
  line-height: var(--erpx-leading-snug);
  color: var(--erpx-color-text-primary);
  cursor: pointer;
  min-width: 0;
}

.e-checkbox__description {
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-xs);
}

.e-checkbox--disabled .e-checkbox__label {
  color: var(--erpx-color-text-disabled);
  cursor: not-allowed;
}
</style>
